import {Component, OnInit} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {EMPTY} from "rxjs";
import {catchError, finalize, switchMap} from "rxjs/operators";
import {localizationKey, LocalizationStrings} from "../../../i18n/i18n-model";
import {ActionButtonState} from "../../../tools/confirm-action-button/pre-confirm-action-button.component";
import {PropertyChangeListener} from "../../../tools/form/form-field-config";
import {NavigationRoute} from "../../../tools/navigation/navigation-route";
import {RouteNavigator} from "../../../tools/navigation/route-navigator.service";
import {Notifications, NotificationType} from "../../../tools/notifications/notifications";
import {Analysis, createUploadFormConfig, DoctorFindings} from "../analysis";
import {AnalysisService} from "../analysis.service";
import {FinishAnalysisService} from "../finish-analysis.service";
import {MeasurementIdProvider} from "../measurement-id-provider";
import {LanguageProvider} from "../../../i18n/language-provider";
import {SleepReportData, sleepReportDataFrom, SleepReportSampleImage} from "../sleep-report/sleep-report-data";
import {SleepReportGenerator} from "../sleep-report/sleep-report-generator.service";
import {sleepFindingsText} from "../sleep-report/sleep-findings-text";
import {I18nService} from "../../../i18n/i18n.service";
import {SleepFindingsFormConfig} from "./sleep-findings-form-config";
import {SleepAnalysisValidator} from "./validate-sleep-analysis";
import {UploadFormConfig} from "../../upload/upload-form-config";

@Component({
  selector: "app-sleep-analysis",
  templateUrl: "./sleep-analysis.component.html",
  standalone: false
})
export class SleepAnalysisComponent implements OnInit, PropertyChangeListener<DoctorFindings> {
  sleepAnalysis?: Analysis;
  sleepFindingsFormConfig?: SleepFindingsFormConfig;
  patientDataFields?: UploadFormConfig;

  analysisLoadingInProgress = true;
  saveInProgress = false;
  previewInProgress = false;
  finishButtonState: ActionButtonState = "INITIAL";
  errorMessage?: keyof LocalizationStrings;
  readonly excludedSampleImages = new Set<SleepReportSampleImage>();

  private measurementId?: number;
  private findingsValidator?: SleepAnalysisValidator;

  constructor(
    private readonly measurementIdProvider: MeasurementIdProvider,
    private readonly analysisService: AnalysisService,
    private readonly notifications: Notifications,
    private readonly sleepReportGenerator: SleepReportGenerator,
    private readonly finishAnalysisService: FinishAnalysisService,
    private readonly navigator: RouteNavigator,
    private readonly route: ActivatedRoute,
    private readonly languageProvider: LanguageProvider,
    private readonly i18nService: I18nService
  ) {
  }

  ngOnInit(): void {
    this.measurementId = this.measurementIdProvider.getId(this.route)!!;

    this.analysisService
      .getAnalysis(this.measurementId)
      .pipe(finalize(() => this.analysisLoadingInProgress = false))
      .subscribe((analysis) => {
        this.languageProvider.setReportLanguage(analysis.reportLanguage);
        this.sleepAnalysis = analysis;
        this.generateFindingsWhenBlank(analysis);
        this.patientDataFields = createUploadFormConfig(analysis);
        this.findingsValidator = new SleepAnalysisValidator(analysis.doctorFindings);
        this.sleepFindingsFormConfig = new SleepFindingsFormConfig(
          analysis.doctorFindings,
          this,
          property => this.findingsValidator!.isPropertyValid(property)
        );
      });
  }

  private generateFindingsWhenBlank(analysis: Analysis): void {
    if (this.hasWrittenFindings(analysis.doctorFindings)) {
      return;
    }

    analysis.doctorFindings.findings = this.generatedFindings(analysis);
  }

  handlePropertyChange(property: keyof DoctorFindings, newValue: any): void {
    this.sleepAnalysis!.doctorFindings[property] = newValue;
    this.findingsValidator!.updatePropertyValidation(property);
    this.updateErrorMessageForValidation();
  }

  onGenerateFindingsButtonClick(): void {
    const doctorFindings = this.sleepAnalysis!.doctorFindings;
    const generatedFindings = this.generatedFindings(this.sleepAnalysis!);

    this.handlePropertyChange(
      "findings",
      this.hasWrittenFindings(doctorFindings)
        ? `${generatedFindings}\n\n${doctorFindings.findings}`
        : generatedFindings
    );
  }

  private generatedFindings(analysis: Analysis): string {
    return sleepFindingsText(
      sleepReportDataFrom(analysis),
      key => this.i18nService.getLocalizedString(key, analysis.reportLanguage)
    );
  }

  private hasWrittenFindings(doctorFindings: DoctorFindings): boolean {
    return !!doctorFindings.findings?.trim();
  }

  onToggleSampleInclusion(sampleImage: SleepReportSampleImage): void {
    if (this.excludedSampleImages.has(sampleImage)) {
      this.excludedSampleImages.delete(sampleImage);
    } else {
      this.excludedSampleImages.add(sampleImage);
    }
  }

  onSaveButtonClick(): void {
    this.saveInProgress = true;

    this.analysisService
      .saveAnalysis(this.measurementId!, this.sleepAnalysis!.doctorFindings)
      .pipe(finalize(() => this.saveInProgress = false))
      .subscribe({
        next: () => this
          .notifications
          .addNotification(NotificationType.OK, localizationKey("analysisMessageSaveSuccess")),
        error: () => this
          .notifications
          .addNotification(NotificationType.ERROR, localizationKey("analysisMessageSaveFailed"))
      });
  }

  onPreviewButtonClick(): void {
    this.previewInProgress = true;

    this.sleepReportGenerator
      .generateAndOpenInNewTab(this.sleepReportData())
      .pipe(
        catchError(() => EMPTY),
        finalize(() => this.previewInProgress = false)
      )
      .subscribe();
  }

  onFinishButtonClick(): void {
    this.findingsValidator!.updateAllValidations();

    this.updateErrorMessageForValidation();

    if (this.finishButtonState === "INITIAL") {
      this.finishButtonState = "CONFIRMATION";
      return;
    }

    this.finishButtonState = "ACTION_IN_PROGRESS";

    this.analysisService
      .saveAnalysis(this.measurementId!, this.sleepAnalysis!.doctorFindings)
      .pipe(
        switchMap(() => this.sleepReportGenerator.generate(this.sleepReportData())),
        switchMap(report => this.finishAnalysisService.finishAnalysis(this.measurementId!, report)),
        finalize(() => this.finishButtonState = "INITIAL")
      )
      .subscribe({
        next: () => this.navigator.navigateTo(NavigationRoute.LIST),
        error: () => this.errorMessage = "analysisMessageFinishAnalysisFailed"
      });
  }

  private sleepReportData(): SleepReportData {
    const reportData = sleepReportDataFrom(this.sleepAnalysis!);
    const images = reportData.externalFindings.images;

    return {
      ...reportData,
      externalFindings: {
        ...reportData.externalFindings,
        images: {
          ...images,
          sampleImages: images.sampleImages.filter(sampleImage => !this.excludedSampleImages.has(sampleImage))
        }
      }
    };
  }

  private updateErrorMessageForValidation() {
    if (this.findingsValidator!.isFormValid()) {
      this.errorMessage = undefined;
    } else {
      this.errorMessage = "analysisFormValidationError";
    }
  }
}
